import { ReactNode, useMemo, useState } from 'react';
import {
  Box,
  Button,
  ToggleButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  ToggleButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { deepPurple } from '@mui/material/colors';

import {
  ColumnFilledIcon,
  ColumnFreeIcon,
  DefaultFilledIcon,
  DefaultFreeIcon,
  DefaultTextIcon,
  ListTextIcon,
  MediaFilledIcon,
  PriceDownTextIcon,
  PriceUpTextIcon,
  SquareMiddlePriceIcon,
} from './assets';

interface IPreviewWidget {
  id: string;
  direction: 'horizontal' | 'vertical' | 'all';
  src: ReactNode;
}

export const previewList: IPreviewWidget[] = [
  {
    id: 'default-text',
    direction: 'horizontal',
    src: DefaultTextIcon,
  },
  {
    id: 'list-text',
    direction: 'horizontal',
    src: ListTextIcon,
  },
  {
    id: 'price-down-text',
    direction: 'vertical',
    src: PriceDownTextIcon,
  },
  {
    id: 'price-up-text',
    direction: 'vertical',
    src: PriceUpTextIcon,
  },
  {
    id: 'default-filled',
    direction: 'horizontal',
    src: DefaultFilledIcon,
  },
  {
    id: 'default-free',
    direction: 'horizontal',
    src: DefaultFreeIcon,
  },
  {
    id: 'column-filled',
    direction: 'vertical',
    src: ColumnFilledIcon,
  },
  {
    id: 'column-free',
    direction: 'vertical',
    src: ColumnFreeIcon,
  },
  {
    id: 'media-filled',
    direction: 'vertical',
    src: MediaFilledIcon,
  },
  {
    id: 'square-middle-price',
    direction: 'vertical',
    src: SquareMiddlePriceIcon,
  },
];

interface PreviewProps {
  previewWidget: IPreviewWidget['id'];
  onChange: ({
    previewWidget,
  }: {
    previewWidget: IPreviewWidget['id'];
  }) => void;
}

const PreviewWidget = ({
  previewWidget = 'default-filled',
  onChange,
}: PreviewProps) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedWidget, setSelectedWidget] =
    useState<IPreviewWidget['id']>(previewWidget);

  const [selectedDirection, setSelectedDirection] =
    useState<IPreviewWidget['direction']>('all');

  const handleOpen = () => {
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
  };

  const handleChange = (selectedWidget: IPreviewWidget['id']) => {
    setSelectedWidget(selectedWidget);
  };

  const onSave = () => {
    onChange((props: { previewWidget: IPreviewWidget['id'] }) => {
      props.previewWidget = selectedWidget;
      return props;
    });
    handleClose();
  };

  const handleDirectionChange = (e: any) => {
    const value = e.target.value;
    setSelectedDirection(value);
  };

  const widgetThumbnail = useMemo(() => {
    const preview = previewList.find((p) => p.id === previewWidget);
    return preview?.src ?? '';
  }, [previewWidget]);

  const previewDirection = useMemo(() => {
    if (!previewList) return [];
    if (selectedDirection === 'all' || !selectedDirection) return previewList;
    return previewList.filter(
      ({ direction }) => direction === selectedDirection
    );
  }, [selectedDirection]);

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          maxHeight: 180,
          maxWidth: 500,
          gap: '16px',
        }}
      >
        <img src={widgetThumbnail as string} alt="preview-widget" />
      </Box>
      <Button onClick={handleOpen}>Cambiar Vista Previa</Button>

      <Dialog open={openDialog} onClose={handleClose} maxWidth={'lg'} fullWidth>
        <Stack
          direction={'row'}
          justifyContent={'space-between'}
          alignItems={'center'}
          p={2}
        >
          <DialogTitle>Elige un Diseño</DialogTitle>
          <Stack>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Stack>
        </Stack>
        <DialogContent>
          <Stack spacing={2}>
            <ToggleButtonGroup
              value={selectedDirection}
              exclusive
              onChange={handleDirectionChange}
              fullWidth={false}
            >
              <ToggleButton value={'all'}>Todas</ToggleButton>
              <ToggleButton value={'horizontal'}>Horizontales</ToggleButton>
              <ToggleButton value={'vertical'}>Verticales</ToggleButton>
            </ToggleButtonGroup>
            <Grid container columnSpacing={2} rowSpacing={2}>
              {previewDirection.map(({ id, src }) => (
                <Grid item key={id} xs={12} md={6} lg={4}>
                  <Box
                    sx={{
                      minHeight: 275,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      backgroundColor: deepPurple[50],
                      borderRadius: 2,
                      ':hover': {
                        cursor: 'pointer',
                      },
                      border: '2px solid transparent',
                      borderColor:
                        selectedWidget === id ? deepPurple[500] : 'transparent',
                    }}
                    onClick={() => handleChange(id)}
                  >
                    <img src={src as string} alt="Thumbnail" />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="outlined" onClick={handleClose}>
            Cancelar
          </Button>
          <Button onClick={onSave}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default PreviewWidget;
